// Customer referral program.
// - Each profile has a unique referral_code (auto-generated by trigger).
// - Sharing the code (or link with ?ref=CODE) attributes new signups.
// - First paid + completed booking by a referred customer awards
//   £15 credit to the referrer and £10 to the referee via loyalty_points.
// - Credit can be spent against any future booking before checkout.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type ReferralRow = {
  id: string;
  referred_email: string | null;
  referred_name: string | null;
  status: string;
  created_at: string;
  completed_at: string | null;
  referrer_reward: number | null;
};

export type LedgerRow = {
  id: string;
  points: number;
  reason: string;
  balance_after: number;
  created_at: string;
};

export type ReferralSummary = {
  code: string;
  share_url: string;
  credit_balance: number;
  referrer_reward: number;
  referee_reward: number;
  referrals: ReferralRow[];
  ledger: LedgerRow[];
};

const APP_URL = (process.env.PUBLIC_APP_URL || "https://peopels.com").replace(/\/$/, "");

export const getMyReferralSummary = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ReferralSummary> => {
    const { supabase, userId } = context;

    const [profile, balance, referrals, ledger] = await Promise.all([
      supabase.from("profiles").select("referral_code").eq("id", userId).maybeSingle(),
      supabase.rpc("my_referral_credit_balance"),
      supabase
        .from("referrals")
        .select("id, referred_id, referred_email, status, created_at, completed_at, referrer_reward")
        .eq("referrer_id", userId)
        .order("created_at", { ascending: false })
        .limit(50),
      supabase
        .from("loyalty_points")
        .select("id, points, reason, balance_after, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(30),
    ]);

    const code = profile.data?.referral_code ?? "";
    const referredIds = (referrals.data ?? [])
      .map((r: any) => r.referred_id)
      .filter(Boolean) as string[];
    const nameMap = new Map<string, string>();
    if (referredIds.length) {
      const { data: names } = await supabase
        .from("profiles")
        .select("id, display_name, full_name")
        .in("id", referredIds);
      for (const n of names ?? []) {
        nameMap.set((n as any).id, (n as any).display_name || (n as any).full_name || "");
      }
    }

    return {
      code,
      share_url: code ? `${APP_URL}/auth?mode=signup&ref=${code}` : "",
      credit_balance: Number(balance.data ?? 0),
      referrer_reward: 15,
      referee_reward: 10,
      referrals: (referrals.data ?? []).map((r: any) => ({
        id: r.id,
        referred_email: r.referred_email,
        referred_name: nameMap.get(r.referred_id) ?? null,
        status: r.status,
        created_at: r.created_at,
        completed_at: r.completed_at,
        referrer_reward: r.referrer_reward != null ? Number(r.referrer_reward) : null,
      })),
      ledger: (ledger.data ?? []).map((l: any) => ({
        id: l.id,
        points: Number(l.points),
        reason: l.reason,
        balance_after: Number(l.balance_after),
        created_at: l.created_at,
      })),
    };
  });

export const claimReferralCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ code: z.string().trim().min(4).max(16) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: result, error } = await supabase.rpc("claim_referral_signup", {
      _code: data.code.toUpperCase(),
    });
    if (error) throw new Error(error.message);
    return result as { claimed: boolean; reason?: string; referrer_id?: string };
  });

export const applyReferralCredit = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ bookingId: z.string().uuid(), amount: z.number().min(0).max(10000) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: result, error } = await supabase.rpc("apply_referral_credit", {
      _booking_id: data.bookingId,
      _amount: data.amount,
    });
    if (error) throw new Error(error.message);
    return result as { applied: number; new_total: number; remaining_balance: number };
  });
